import {Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {PageHeaderComponent} from '../util/design/page-header/page-header.component';
import {
  AI_INSTRUCTION_MODULE_ID,
  buildCategoriesFromModules,
  buildDataOutputSchema,
  buildEdgesFromManifest,
  buildFlowOutPorts,
  buildParameterOutputSchema,
  collectHybridCompletenessErrors,
  collectMissingInputs,
  collectMissingSettings,
  applyUpstreamPrefill,
  FlowEdge,
  getNodeName,
  HYBRID_MODULE_CATEGORIES,
  HYBRID_SHARED_CATEGORIES,
  MODULE_CATEGORIES,
  ModuleCategoryInfo,
  NUTZERANTWORT_MODULE_ID,
  PALETTE_HIDDEN_MODULE_IDS,
  reconcileEingangParameters,
  resolveBranchSpec,
  SetParamDef,
  syncEdgesToManifest
} from './workflow.models';
import {WorkflowService} from '../../services/workflow.service';
import {RulesetService} from '../../services/ruleset.service';
import {RuleInterpretationService} from '../../services/rule-interpretation.service';
import {WorkflowCanvasComponent} from './workflow-canvas.component';
import {SIDEBAR_OVERLAY_WIDTH} from './workflow-canvas.utils';
import {WorkflowSidebarComponent} from './workflow-sidebar.component';
import {WorkflowNodeConfigComponent} from './workflow-node-config.component';
import {WorkflowTestPanelComponent} from './workflow-test-panel.component';
import {WorkflowLogPanelComponent} from './workflow-log-panel.component';
import {StateConfigService} from '../../services/state/state-config.service';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faChevronLeft,
  faCircleCheck,
  faCircleXmark,
  faClone,
  faCopy,
  faDiagramProject,
  faExpand,
  faMagnet,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faPaste,
  faPlay,
  faRotateLeft,
  faRotateRight,
  faSpinner,
  faTrashCan,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {
  BicButtonComponent,
  BicPopupComponent,
  BicTextareaComponent,
  BicToastService,
  TooltipDirective
} from '@buildinginformationcloud/bic-ui-components';
import {FormsModule} from '@angular/forms';

import {WorkflowDefinition} from '../../api/model/workflowDefinition';
import {WorkflowInstanceDto} from '../../api/model/workflowInstanceDto';
import {StepLogDto} from '../../api/model/stepLogDto';
import {Manifest} from '../../api/model/manifest';
import {ModuleManifest} from '../../api/model/moduleManifest';
import {StepNode} from '../../api/model/stepNode';
import {TriggerNode} from '../../api/model/triggerNode';
import {Ruleset} from '../ruleManagement/models/rule-management.models';
import {createMemo} from '../../memo';
import {Subscription} from 'rxjs';
import {RuleTestService} from '../../services/rule-test/rule-test.service';
import {RuleTestPopupContext, RuleTestQuota, ruleTestContextKey} from '../../services/rule-test/rule-test.models';
import {ruleTestLimitTooltip} from '../../services/rule-test/rule-test.format';
import {RuleTestPopupComponent} from '../ruleManagement/shared/rule-test-popup/rule-test-popup.component';
import {buildDefaultReportConfigForRuleset} from '../my-checks/my-checks-builder/my-checks-builder.model';
import {ChatService} from '../chatBIC/service/chat.service';
import {MyCheckProposal, MyCheckProposalService} from '../../services/mycheck-proposal.service';
import {ProposalBannerComponent} from '../util/design/proposal-banner/proposal-banner.component';
import {computeRuleProposalDiff, RuleProposalMark} from './rule-proposal-diff';

interface DrillDownEntry {
  definitionId: string;
  definition: WorkflowDefinition;
  manifest: Manifest;
  edges: FlowEdge[];
  currentInstance: WorkflowInstanceDto | null;
  hybridChildInstances: WorkflowInstanceDto[];
  workflowName: string;
}

type SidebarView = 'modules' | 'node-config' | 'test-panel' | 'step-detail';

@Component({
  selector: 'app-workflow-editor',
  standalone: true,
  imports: [PageHeaderComponent, WorkflowCanvasComponent, WorkflowSidebarComponent, WorkflowNodeConfigComponent, WorkflowTestPanelComponent, WorkflowLogPanelComponent, FaIconComponent, BicButtonComponent, BicPopupComponent, BicTextareaComponent, TooltipDirective, FormsModule, RuleTestPopupComponent, ProposalBannerComponent],
  templateUrl: './workflow-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./workflow-editor.component.scss'],
})
export class WorkflowEditorComponent implements OnInit, OnDestroy {

  /** Stabile Rueckgabewerte fuer Template-Bindungen - siehe memo.ts. */
  private readonly memo = createMemo();

  /** Die aktuelle id (database PK) aus der Route */
  definitionId: string = '';
  @ViewChild(WorkflowCanvasComponent) canvas!: WorkflowCanvasComponent;
  /** The WorkflowDefinition from the backend */
  definition!: WorkflowDefinition;
  /** The parsed Manifest (the canvas works with this directly) */
  manifest!: Manifest;
  /** All available modules from the registry */
  allModules: ModuleManifest[] = [];
  isHybridMode = false;

  /**
   * Modul-Palette (Drag&Drop-Liste) ohne die Module aus {@link PALETTE_HIDDEN_MODULE_IDS}.
   * Bewusst getrennt von {@code allModules}: bestehende Knoten (z. B. aus vorgefertigten
   * myChecks) müssen weiter über {@code allModules} aufgelöst werden, auch wenn ihr Modul
   * hier nicht mehr auftauchen soll.
   */
  get paletteModules(): ModuleManifest[] {
    // Gecacht, weil das Ergebnis an [modules] haengt (siehe memo.ts). allModules
    // wird beim Laden einmal neu zugewiesen, nie veraendert - die Referenz genuegt.
    return this.memo('paletteModules', [this.allModules],
      () => this.allModules.filter(m => !PALETTE_HIDDEN_MODULE_IDS.has(m.moduleId)));
  }
  readOnly = false;
  /** Admin-Modus: globale HYBRID-Regel ohne Projektkontext (Speichern an globale Endpunkte). */
  adminGlobal = false;
  /** Parent-Regelset-ID (Hybrid-Route :rulesetId/hybrid/:ruleId) — Quelle der Set-Eingabeparameter. */
  rulesetId = '';
  /** Aus dem Regelset geladener Parameter-Kontext für den eingangsparameter-Knoten. */
  hybridSetContext: {
    params: SetParamDef[];
    identifikatorParamId: string;
    pruefebene: string;
    kiModel: string;
    /** Erlaubte Dateitypen DIESER Regel (sonst die des Sets) — entscheidet über den Datei-Modus. */
    allowedFileTypes: string[];
  } | null = null;
  /** View mode: 'edit' for normal editing, 'log' for viewing execution results */
  viewMode: 'edit' | 'log' = 'edit';
  /** The currently selected step log (in log-view mode) */
  selectedStepLog: StepLogDto | null = null;
  edges: FlowEdge[] = [];
  /** Currently selected node for the config panel */
  selectedStep: StepNode | null = null;
  sidebarView: SidebarView = 'modules';
  moduleSearchQuery = '';
  moduleCategories: ModuleCategoryInfo[] = [];
  selectedTrigger: TriggerNode | null = null;
  selectedModule: ModuleManifest | null = null;
  sharedCategories: ModuleCategoryInfo[] = [];
  sidebarTitle = 'Module';
  /** Loading state while API data is fetched */
  loading = true;
  /** Whether an activate/deactivate call is in progress */
  activating = false;
  /** Whether a trigger call is in progress */
  triggering = false;
  /** Whether a save call is in progress */
  saving = false;
  /** Blocking validation messages from the last save attempt (missing required inputs). */
  validationErrors: string[] = [];
  /**
   * Nicht blockierende Hinweise (aktuell: Verzweigungs-Zweige ohne Nutzerantwort). Anders als
   * {@link validationErrors} verhindern sie das Speichern nicht.
   */
  validationWarnings: string[] = [];
  /** The current workflow instance after triggering */
  currentInstance: WorkflowInstanceDto | null = null;
  /** Whether we are polling for instance result */
  instancePolling = false;
  /**
   * Rückfrage, bevor Verzweigungs-Kanten verloren gehen (siehe {@link confirmBranchPortLoss}).
   * `null` = keine Rückfrage offen.
   */
  branchLossMessage: string | null = null;
  private branchLossStep: StepNode | null = null;
  /** Settings, auf die der Abbrechen-Pfad zurückdreht. */
  private branchLossPrevSettings: { [key: string]: any } | null = null;
  /**
   * Settings-Schnappschuss je Knoten, Stand des letzten Canvas-Aufbaus.
   *
   * Nötig, weil {@link onNodeConfigChanged} erst läuft, NACHDEM das Panel den neuen Wert in
   * `step.settings` geschrieben hat — der Vorzustand ist dort nicht mehr ablesbar. Ohne ihn ließe
   * sich nicht sagen, welche Zweige es vorher gab und welche Kanten dadurch verwaisen.
   */
  private settingsSnapshot = new Map<string, string>();
  /** Settings popup state */
  showSettingsPopup = false;
  settingsName = '';
  settingsDescription = '';
  protected stateConfigService = inject(StateConfigService);
  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faRotateLeft = faRotateLeft;
  protected readonly faRotateRight = faRotateRight;
  protected readonly faDiagramProject = faDiagramProject;
  protected readonly faMagnifyingGlassPlus = faMagnifyingGlassPlus;
  protected readonly faMagnifyingGlassMinus = faMagnifyingGlassMinus;
  protected readonly faExpand = faExpand;
  protected readonly faCopy = faCopy;
  protected readonly faPaste = faPaste;
  protected readonly faClone = faClone;
  protected readonly faTrashCan = faTrashCan;
  protected readonly faMagnet = faMagnet;
  /** All child instances for hybrid parent view */
  hybridChildInstances: WorkflowInstanceDto[] = [];
  /** The parent instance when viewing a hybrid result */
  hybridParentInstance: WorkflowInstanceDto | null = null;
  /** Stack for drill-down navigation into sub-workflows */
  drillDownStack: DrillDownEntry[] = [];
  protected readonly faCircleCheck = faCircleCheck;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private flowService = inject(WorkflowService);
  private rulesetService = inject(RulesetService);
  private ruleInterpretation = inject(RuleInterpretationService);
  protected readonly faSpinnerThird = faSpinnerThird;
  /** Polling subscription handle */
  private pollingSubscription: ReturnType<typeof setInterval> | null = null;

  // ─── Testlauf („Prüfregel testen", #2356) ───────────────────────────────────
  private ruleTestService = inject(RuleTestService);
  protected readonly faPlay = faPlay;
  /** Beim Öffnen des Popups EINMAL berechnet (Feld, kein Getter – siehe Frontend/AGENTS.md). */
  ruleTestContext: RuleTestPopupContext | null = null;
  ruleTestQuota: RuleTestQuota | null = null;
  ruleTestUnseen = false;
  private ruleTestKey = '';
  private pendingRuleTestRunId: string | null = null;
  private ruleTestSubscriptions = new Subscription();
  private unregisterRuleTestEditor?: () => void;
  /** Das Hybrid-Set dieser Regel, wie gespeichert – liefert Set-Konfiguration, Name und Regel-Einschränkungen. */
  private ruleTestSetDef: WorkflowDefinition | null = null;
  /** Manifest-Stand direkt nach dem Laden, für „Stand: nicht übernommen". */
  private ruleTestInitialManifest = '';

  /** Get the workflow name for display */
  get workflowName(): string {
    return this.manifest?.name ?? 'Neuer Workflow';
  }

  /** Whether the definition is currently active */
  get isActive(): boolean {
    return this.definition?.status === 'ACTIVE';
  }

  /** Whether the current instance has finished (terminal state) */
  get instanceFinished(): boolean {
    const status = this.currentInstance?.status;
    return status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED';
  }

  get showSidebar(): boolean {
    return true;
  }

  /**
   * Breite, die das schwebende Panel rechts über der Canvas verdeckt.
   *
   * Die Canvas reicht darunter durch – wer mit dem sichtbaren Ausschnitt rechnet (Auto-Panning
   * beim Ziehen, „Ansicht einpassen"), muss diesen Betrag abziehen.
   */
  get sidebarOverlayWidth(): number {
    return this.showSidebar ? SIDEBAR_OVERLAY_WIDTH : 0;
  }

  get hasSelection(): boolean {
    return !!(this.canvas?.selectedNodeId || this.canvas?.selectedNodeIds?.size > 0);
  }
  protected readonly faCircleXmark = faCircleXmark;

  /** Build logs from steps + outputData.logs for the log panel */
  get instanceLogs(): any[] {
    const logs: any[] = [];

    // Logs from outputData.logs (module-provided)
    const outputLogs = (this.currentInstance?.outputData as any)?.logs;
    if (Array.isArray(outputLogs)) {
      logs.push(...outputLogs);
    }

    // If no explicit logs, generate from steps
    if (logs.length === 0 && this.currentInstance?.steps) {
      for (const step of this.currentInstance.steps) {
        if (step.status === 'COMPLETED' || step.status === 'FAILED' || step.status === 'RUNNING') {
          const moduleName = this.allModules.find(m => m.moduleId === step.moduleId)?.name ?? step.moduleId ?? step.nodeId;
          logs.push({
            timestamp: step.completedAt ?? step.startedAt,
            level: step.status === 'FAILED' ? 'ERROR' : 'INFO',
            title: moduleName,
            message: step.statusMessage ?? (step.status === 'COMPLETED' ? 'Erfolgreich ausgeführt' : step.status === 'FAILED' ? (step.errorMessage ?? 'Fehler aufgetreten') : 'Wird ausgeführt…'),
          });
        }
      }
    }

    return logs;
  }

  /** Build a nodeId → status map from currentInstance steps for canvas highlighting */
  get stepStatuses(): { [nodeId: string]: string } {
    const map: { [nodeId: string]: string } = {};
    if (this.currentInstance?.steps) {
      for (const step of this.currentInstance.steps) {
        if (step.nodeId && step.status) {
          map[step.nodeId] = step.status;
        }
      }
    }
    return map;
  }

  /** Zurück zur vorherigen Übersicht */
  goBack(): void {
    // Wenn wir aus dem Hybrid-Editor kommen, zurück dorthin
    const url = this.route.snapshot.url;
    const fullUrl = this.route.snapshot.pathFromRoot.map(s => s.url.map(u => u.path)).flat();
    if (fullUrl.includes('hybrid')) {
      this.router.navigate(['..'], {relativeTo: this.route});
      return;
    }
    this.router.navigate([
      'projects',
      this.stateConfigService.selectedProject.id,
      'workflows',
    ]);
  }
  protected readonly faSpinner = faSpinner;
  private toastService = inject(BicToastService);
  private chatService = inject(ChatService);
  private proposalService = inject(MyCheckProposalService);
  private destroyRef = inject(DestroyRef);

  // ─── chatBIC-Vorschlag für diese Regel (Propose-Hybrid-Rule-Change) ───────────
  /** Offener Vorschlag – sein Manifest steht bereits auf der Leinwand. */
  pendingProposal: MyCheckProposal | null = null;
  /** Neue/geänderte Knoten des Vorschlags, als Markierung auf der Leinwand. */
  proposalMarks: { [nodeId: string]: RuleProposalMark } = {};
  /** Namen der Knoten, die der Vorschlag entfernt (auf der Leinwand nicht mehr sichtbar). */
  proposalRemovedNames: string[] = [];
  /** Stand vor dem Vorschlag – „Verwerfen" stellt genau ihn wieder her. */
  private preProposalManifest: string | null = null;
  protected readonly faXmark = faXmark;

  onNodeConfigClosed() {
    this.selectedStep = null;
    this.selectedTrigger = null;
    this.selectedModule = null;
    this.sidebarView = 'modules';
  }

  /**
   * Nach einer Änderung im Konfig-Panel: Undo-Schnappschuss setzen, Karte nachziehen und – wenn
   * sich dadurch Ports verschieben – die Leinwand neu aufbauen.
   */
  onNodeConfigChanged(event: { key: string; value: any }): void {
    // Vor der Auswertung: die Änderung hat `inputs`/`settings` schon mutiert, der Schnappschuss
    // muss den Stand DAVOR gar nicht kennen — er sichert den aktuellen Stand für ein späteres
    // Strg+Z. Ohne ihn hätte eine Panel-Eingabe keinen eigenen Undo-Schritt.
    this.canvas?.pushSnapshotForPanel();

    // Wertbindungen (nicht-Settings) ändern keine Ports, aber die Zusammenfassung auf der Karte.
    if (!event.key.startsWith('__settings.')) {
      this.canvas?.refreshNode(this.selectedStep?.nodeId);
      return;
    }
    // Eine Settings-Änderung an einem Schritt kann Ports verschieben: die Anzahl der Var-Ports
    // (x-varCountSource), dynamische Datenports (x-dynamic-properties) oder Flow-Ausgänge
    // (x-branch). Alles drei steht im Manifest, deshalb keine Modul-ID-Sonderfälle mehr.
    //
    // Neu gezeichnet wird nur, wenn sich die Port-Signatur wirklich ändert. Ohne diese Prüfung
    // liefe der Aufbau bei JEDEM Feld — also auch bei jedem Tastendruck im Prompt-Textfeld.
    if (this.selectedStep) {
      if (!this.portsSignatureChanged(this.selectedStep)) {
        // Kein Portwechsel, aber die Karte kann sich geändert haben (x-node-summary).
        this.canvas?.refreshNode(this.selectedStep.nodeId);
        return;
      }
      // Verschwindet ein verbundener Verzweigungs-Ausgang, erst fragen — sonst wäre die Kante beim
      // nächsten Speichern lautlos weg.
      if (this.confirmBranchPortLoss(this.selectedStep)) return;
      this.rebuildCanvasFromManifest();
      return;
    }

    const trigger = this.manifest?.trigger;
    if (!trigger || this.selectedTrigger !== trigger) return;
    if ((trigger.moduleId ?? '').toLowerCase() !== 'eingangsparameter') return;
    if (event.key !== '__settings.parameter' && event.key !== '__settings.pruefebene') return;

    const cfg = trigger.config ?? (trigger.config = {});
    cfg.outputSchema = buildParameterOutputSchema(cfg);
    this.rebuildCanvasFromManifest();
  }

  /**
   * Kantenverlust bestätigt: die verwaisten Zweig-Kanten aus dem Manifest entfernen.
   *
   * Das erledigt `syncEdgesToManifest` selbst: für die weggefallenen Zweige liefert
   * resolveBranchSpec keinen Port mehr, `onBranch` wird entsprechend neu geschrieben und die Kanten
   * der verschwundenen Ports fallen aus den Flow-Kanten. Danach neu zeichnen — die Port-0-Kante
   * bleibt als `onSuccess` erhalten. Alle übrigen Kanten der Leinwand werden mitgeschrieben, damit
   * zwischenzeitliche Änderungen nicht verloren gehen.
   */
  onBranchLossConfirm(): void {
    this.closeBranchLossPopup();
    syncEdgesToManifest(this.manifest, this.canvas?.edges ?? this.edges, this.allModules);
    this.rebuildCanvasFromManifest();
  }

  /**
   * Kantenverlust abgebrochen: die Settings des Knotens auf den Schnappschuss zurückdrehen und aus
   * dem Manifest neu zeichnen. Das Manifest wurde nicht synchronisiert, trägt also noch sein
   * `onBranch` — Ports und Kanten kommen damit unverändert zurück.
   *
   * Bewusst der ganze Settings-Stand und nicht ein einzelner Schlüssel: welches Setting die
   * Verzweigung getragen hat, weiß nur das Manifest des Moduls.
   */
  onBranchLossCancel(): void {
    if (this.branchLossStep && this.branchLossPrevSettings) {
      this.branchLossStep.settings = this.branchLossPrevSettings;
    }
    this.closeBranchLossPopup();
    this.rebuildCanvasFromManifest();
  }

  /** Save workflow to backend */
  saveWorkflow(closeAfterSave = true): void {
    if (this.readOnly || this.saving) return;
    const projectId = this.stateConfigService.selectedProject?.id ?? 0;
    if (!this.adminGlobal && !projectId) return;

    const isNew = !this.definitionId || this.definitionId === 'new';

    // Sync canvas edges back into manifest onSuccess/onError before saving
    // Read edges directly from canvas component since its reference may have diverged
    const currentEdges = this.canvas?.edges ?? this.edges;
    syncEdgesToManifest(this.manifest, currentEdges, this.allModules);

    // Pflicht-Eingänge prüfen, bevor gespeichert wird. Seit Eingänge auch einen Festwert
    // tragen können, ist "nicht verbunden" nur dann ein Fehler, wenn auch kein Festwert
    // gesetzt ist — vorher war das gar nicht prüfbar und fiel erst zur Laufzeit auf.
    // Bewusst hier (nicht in persistWorkflow), damit der Admin-Pfad ohne Backend-Validierung
    // ebenfalls greift und die KI-Interpretation nicht umsonst läuft.
    // Dazu unvollständige Einstellungen (z.B. Relationsprüfung ohne IFC-Klasse) – die fielen sonst
    // erst im Modul auf, als technisch fehlgeschlagener Lauf.
    this.validationErrors = [
      ...collectMissingInputs(this.manifest, this.allModules),
      ...collectMissingSettings(this.manifest, this.allModules),
    ];
    if (this.validationErrors.length) {
      this.toastService.warning(this.validationErrors.length === 1
        ? this.validationErrors[0]
        : 'Bitte fehlende Pflichtangaben ergänzen');
      return;
    }

    if (this.isHybridMode) {
      const hybridErrors = collectHybridCompletenessErrors(this.manifest, this.allModules, currentEdges);
      this.validationWarnings = [];
      if (hybridErrors.length) {
        this.validationErrors = [...this.validationErrors, ...hybridErrors];
        this.toastService.warning(hybridErrors[0]);
        return;
      }
    } else {
      // Nicht blockierend: nur ein Hinweis, dass ein Zweig kein Ergebnis liefert (siehe
      // collectDeadEndBranches). Speichern ist weiterhin erlaubt.
      //
      // Zusätzlich als Toast, weil „Übernehmen" den Editor schließt und der Kasten unter der
      // Kopfzeile dann nie zu sehen wäre. Der Kasten bleibt für Teil-Speicherungen, bei denen der
      // Editor offen bleibt.
      this.validationWarnings = this.collectDeadEndBranches(currentEdges);
      if (this.validationWarnings.length) {
        this.toastService.warning(this.validationWarnings.length === 1
          ? this.validationWarnings[0]
          : `${this.validationWarnings.length} Verzweigungen enden ohne Nutzerantwort und bleiben im Bericht unbewertet.`);
      }
    }

    this.saving = true;

    // Erst speichern, dann interpretieren: `interpretRule` löst serverseitig ZWEI sequenzielle
    // LLM-Aufrufe aus (Schritte + Ergebnisse, Timeout je 90 s). Solange das im Speicherpfad hing,
    // dauerte ein Speichervorgang bis zu einer Minute, obwohl der PUT selbst billig ist.
    // Best-effort-Nachtrag: schlägt er fehl, ist die Regel gespeichert und die Übersicht fällt auf
    // Modulnamen bzw. die rohen Ergebnisse zurück.
    this.persistWorkflow(projectId, isNew, () => this.interpretRuleAfterSave(projectId), closeAfterSave);
  }

  ngOnInit() {
    // Vorschlag aus dem chatBIC-Edit-Modus für genau diese Regel (der Server meldet die Regel-ID).
    this.chatService.mycheckProposalReceived
      .pipe(filter(event => !!this.definitionId && event.rulesetId === this.definitionId),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.loadRuleProposal(event.proposalId));

    // In der Hybrid-Route heißt der Parameter 'ruleId', im Flow-Editor 'definitionId'
    this.definitionId = this.route.snapshot.paramMap.get('definitionId')
      ?? this.route.snapshot.paramMap.get('ruleId')
      ?? '';
    this.rulesetId = this.route.snapshot.paramMap.get('rulesetId') ?? '';

    // Detect hybrid mode from route
    const fullUrl = this.route.snapshot.pathFromRoot.map(s => s.url.map(u => u.path)).flat();
    this.isHybridMode = fullUrl.includes('hybrid');
    this.readOnly = this.route.snapshot.queryParams['readOnly'] === 'true';
    this.adminGlobal = this.route.snapshot.data['adminGlobal'] === true;
    this.initRuleTest();

    if (this.isHybridMode) {
      this.sharedCategories = HYBRID_SHARED_CATEGORIES;
      this.sidebarTitle = 'Module Prüfregel';
    } else {
      this.sharedCategories = [];
      this.sidebarTitle = 'Module';
    }

    // Load modules first, then build categories dynamically and load the workflow definition
    this.flowService.listModules().subscribe(modules => {
      this.allModules = modules;

      // Build categories dynamically from actual backend modules, filtered by moduleType
      const moduleTypeFilter = this.isHybridMode ? 'PRUEFREGEL' : 'WORKFLOW';
      const dynamicCategories = buildCategoriesFromModules(modules, moduleTypeFilter);
      this.moduleCategories = dynamicCategories.length > 0
        ? dynamicCategories
        : (this.isHybridMode ? HYBRID_MODULE_CATEGORIES : MODULE_CATEGORIES);

      if (this.definitionId === 'new') {
        this.manifest = this.flowService.createEmptyManifest();
        // Prüfregeln haben keinen separaten Trigger: der Eingangsparameter-Knoten ist der Einstieg
        // und liefert die extrahierten Parameter als Ausgänge für die nachfolgenden Module.
        if (this.isHybridMode && this.manifest.trigger) {
          this.manifest.trigger.moduleId = 'eingangsparameter';
          const cfg = this.manifest.trigger.config ?? (this.manifest.trigger.config = {});
          cfg.outputSchema = buildParameterOutputSchema(cfg);
        }
        this.edges = buildEdgesFromManifest(this.manifest, this.allModules);
        this.loading = false;
        return;
      }

      const loadRuleDefinition = () => this.flowService.getDefinition(this.definitionId).subscribe(def => {
        this.definition = def;
        this.manifest = this.flowService.parseManifest(def);

        // Migration: Prüfregel-Einstieg ist der Eingangsparameter-Knoten (kein separater Trigger mehr).
        // Alte MANUAL_TRIGGER werden umgestellt; die Output-Ports werden aus den konfigurierten
        // Parametern regeneriert (= genau die vom Nutzer definierten Ausgabeparameter).
        if (this.isHybridMode && this.manifest.trigger) {
          const cfg = this.manifest.trigger.config ?? (this.manifest.trigger.config = {});
          const mid = (this.manifest.trigger.moduleId ?? '').toLowerCase();
          if (mid === 'manual_trigger' || mid === '') {
            this.manifest.trigger.moduleId = 'eingangsparameter';
            delete cfg.__name; // Name stammt aus dem Modul-Manifest ('Eingangsparameter')
          }
          if ((this.manifest.trigger.moduleId ?? '').toLowerCase() === 'eingangsparameter') {
            // Set-getrieben: Parameter/Prüfebene/Identifikator vom Regelset abgleichen
            // (Definition folgt dem Set, per-Regel bleibt nur der hidden-Zustand).
            if (this.hybridSetContext) {
              cfg.parameter = reconcileEingangParameters(
                cfg.parameter, this.hybridSetContext.params, this.hybridSetContext.identifikatorParamId,
              );
              cfg.identifikatorParamId = this.hybridSetContext.identifikatorParamId;
              cfg.pruefebene = this.hybridSetContext.pruefebene;
              cfg.erlaubte_dateitypen = this.hybridSetContext.allowedFileTypes;
            }
            cfg.outputSchema = buildParameterOutputSchema(cfg);
          }
        }
        // Set-Modell in die KI-Anweisung-Knoten spiegeln, damit der read-only Badge den im
        // Regelset gepflegten Wert zeigt und nicht den beim Anlegen eingefrorenen.
        this.applySetAiModel();

        this.edges = buildEdgesFromManifest(this.manifest, this.allModules);
        this.loading = false;
        this.ruleTestInitialManifest = this.ruleTestKey ? JSON.stringify(this.ruleTestManifestSnapshot()) : '';
        this.openPendingRuleTest();

        // If instanceId query param is present, enter log-view mode
        const queryInstanceId = this.route.snapshot.queryParams['instanceId'];
        if (!queryInstanceId) this.loadPendingRuleProposal();
        if (queryInstanceId) {
          this.viewMode = 'log';
          this.flowService.getInstance(queryInstanceId).subscribe({
            next: (instance) => {
              // Check if this is a hybrid parent instance (has HYBRID_RULESET_EXECUTOR step)
              const isHybridParent = instance.steps?.some(s => s.moduleId === 'HYBRID_RULESET_EXECUTOR');
              if (isHybridParent) {
                this.loadHybridChildView(instance);
              } else {
                this.currentInstance = instance;
                this.sidebarView = 'test-panel';
                this.fitCanvasDelayed();
              }
            }
          });
        }
      });

      // Hybrid: zuerst die Set-Eingabeparameter laden (für den Abgleich des eingangsparameter-Knotens),
      // dann die Regel-Definition. Ohne Set-Kontext (Fehler/kein Hybrid) lädt die Regel unverändert.
      if (this.isHybridMode && this.rulesetId && this.definitionId && this.definitionId !== 'new') {
        this.flowService.getDefinition(this.rulesetId).subscribe({
          next: setDef => {
            this.hybridSetContext = this.extractSetParamContext(setDef);
            this.ruleTestSetDef = setDef;
            loadRuleDefinition();
          },
          error: () => {
            this.hybridSetContext = null;
            loadRuleDefinition();
          },
        });
      } else {
        this.hybridSetContext = null;
        loadRuleDefinition();
      }
    });
  }

  /**
   * Ein abgelegter eingangsparameter wurde als Einstiegs-Trigger (wieder)hergestellt.
   * Gegen die Regelset-Parameter abgleichen (Definition + Sichtbarkeit) und Ausgänge neu bauen.
   */
  onEingangTriggerDropped(): void {
    const trigger = this.manifest?.trigger;
    if (!trigger || (trigger.moduleId ?? '').toLowerCase() !== 'eingangsparameter') return;
    const cfg = trigger.config ?? (trigger.config = {});
    if (this.hybridSetContext) {
      cfg.parameter = reconcileEingangParameters(
        cfg.parameter, this.hybridSetContext.params, this.hybridSetContext.identifikatorParamId,
      );
      cfg.identifikatorParamId = this.hybridSetContext.identifikatorParamId;
      cfg.pruefebene = this.hybridSetContext.pruefebene;
      cfg.erlaubte_dateitypen = this.hybridSetContext.allowedFileTypes;
    }
    cfg.outputSchema = buildParameterOutputSchema(cfg);
    this.rebuildCanvasFromManifest();
  }

  /** Recompute edges from the manifest and re-render the canvas graph. */
  private rebuildCanvasFromManifest(): void {
    this.edges = buildEdgesFromManifest(this.manifest, this.allModules);
    if (this.canvas) {
      this.canvas.manifest = this.manifest;
      this.canvas.edges = this.edges;
      this.canvas.renderedNodes = [];
      this.canvas.buildGraph();
    }
    this.captureSettingsSnapshot();
  }

  /**
   * Port-Signatur eines Knotens: alles, was seine Ports ausmacht, in einem String.
   *
   * Vergleichsbasis für „muss die Leinwand neu gezeichnet werden?". Bewusst über die echten
   * Port-Builder statt über eine Liste relevanter Settings-Keys — damit gilt es automatisch für
   * jedes Modul, das dynamische Ports deklariert.
   */
  private portsSignature(step: StepNode, settings?: { [key: string]: any }): string {
    const mod = this.allModules.find(m => m.moduleId === step.moduleId);
    const effective = settings ?? step.settings ?? {};
    const flow = buildFlowOutPorts(mod, effective).map(p => p.name);
    const dataOut = Object.keys(buildDataOutputSchema(step.moduleId, effective, mod?.outputSchema)?.properties ?? {});
    // Eingangsports sind konstant (nur der Flow-In) — nur Flow-Ausgänge und die dynamischen
    // Ausgaben (Kandidaten des Wert-Pickers) können sich ändern.
    return JSON.stringify([flow, dataOut]);
  }

  /** True, wenn die Port-Signatur des Knotens gegen den Schnappschuss abweicht. */
  private portsSignatureChanged(step: StepNode): boolean {
    const prevSettings = JSON.parse(this.settingsSnapshot.get(step.nodeId ?? '') ?? '{}');
    return this.portsSignature(step, prevSettings) !== this.portsSignature(step);
  }

  /** Settings-Schnappschuss aller Knoten auf den aktuellen Stand bringen. */
  private captureSettingsSnapshot(): void {
    this.settingsSnapshot.clear();
    for (const step of this.manifest?.steps ?? []) {
      if (step.nodeId) this.settingsSnapshot.set(step.nodeId, JSON.stringify(step.settings ?? {}));
    }
  }

  /** Update the rendered node label on the canvas in real-time when name is changed in sidebar */
  onNodeNameChanged(name: string): void {
    const nodeId = this.selectedStep?.nodeId ?? this.selectedTrigger?.nodeId ?? this.manifest?.trigger?.nodeId;
    if (!nodeId || !this.canvas) return;
    const renderedNode = this.canvas.renderedNodes.find(n => n.id === nodeId);
    if (renderedNode) {
      renderedNode.label = name;
    }
  }

  /**
   * When a ruleset is selected from the config panel, store it as a reference
   * in the current CHECKER node's inputs. The backend execution engine will
   * handle running the ruleset as a sub-workflow.
   */
  onRulesetAddedToWorkflow(ruleset: Ruleset): void {
    if (!this.selectedStep) return;
    const parentStep = this.selectedStep;

    // Store ruleset reference in the parent step's inputs
    if (!parentStep.inputs) parentStep.inputs = {};
    parentStep.inputs['__rulesetId'] = ruleset.id;
    parentStep.inputs['__rulesetName'] = ruleset.name;
    parentStep.inputs['__rulesetType'] = ruleset.type;
  }

  /** Open settings (placeholder for future implementation) */
  onSettingsClick(): void {
    this.settingsName = this.manifest?.name ?? '';
    this.settingsDescription = this.manifest?.description ?? '';
    this.showSettingsPopup = true;
  }

  /** Close settings popup without saving */
  onSettingsClose(): void {
    this.showSettingsPopup = false;
  }

  /** Save settings and update workflow */
  onSettingsSave(): void {
    if (this.readOnly) return;
    if (this.manifest) {
      this.manifest.name = this.settingsName;
      this.manifest.description = this.settingsDescription;
    }
    this.showSettingsPopup = false;
    // Nur Name/Beschreibung übernehmen — der Editor bleibt offen (anders als beim „Übernehmen").
    this.saveWorkflow(false);
  }

  /**
   * Prüft nach einer Settings-Änderung, ob dadurch Verzweigungs-Kanten verwaisen, und fragt
   * gegebenenfalls zurück.
   *
   * Warum überhaupt: verschwindet ein Flow-Ausgang, würde `rebuildEdges()` die Kante per
   * Index-Clamping auf den letzten (Daten-)Port heften, und `syncEdgesToManifest` verwirft sie dann
   * als Datenkante — die Verbindung wäre beim nächsten Speichern lautlos weg.
   *
   * Früher deckte das nur den Prüfmodus ab. Seit die Zweige aus einer Optionsliste entstehen, ist
   * das Löschen oder Umbenennen einer Option genau derselbe Fall — deshalb generisch: alte Spec aus
   * dem Settings-Schnappschuss, neue aus dem aktuellen Stand, Differenz betrachten.
   *
   * @returns true, wenn eine Rückfrage offen ist (der Aufrufer darf dann NICHT neu zeichnen).
   */
  private confirmBranchPortLoss(step: StepNode): boolean {
    const mod = this.allModules.find(m => m.moduleId === step.moduleId);
    const prevSettings = JSON.parse(this.settingsSnapshot.get(step.nodeId ?? '') ?? '{}');
    const prevSpec = resolveBranchSpec(mod, prevSettings);
    const nextSpec = resolveBranchSpec(mod, step.settings);

    // Ohne vorherige Verzweigung kann nichts verwaisen (Einschalten ist immer harmlos: die
    // bestehende Kante bleibt auf Port 0).
    if (!prevSpec) return false;

    const nextKeys = new Set((nextSpec?.cases ?? []).map(c => c.key));
    const nextFlowCount = nextSpec ? nextSpec.cases.length : 1;

    const orphaned = (this.edges ?? []).filter(e => {
      if (e.source !== step.nodeId || e.targetPortIndex !== 0) return false;
      // Port existiert nicht mehr …
      if (e.sourcePortIndex >= nextFlowCount) return true;
      // … oder er existiert, gehört aber zu einem Zweig, den es nicht mehr gibt (Option umbenannt).
      const prevCase = prevSpec.cases[e.sourcePortIndex];
      return !!prevCase && !nextKeys.has(prevCase.key);
    });

    if (!orphaned.length) return false;

    const branchNames = orphaned.map(e =>
      prevSpec.cases[e.sourcePortIndex]?.label ?? `Ausgang ${e.sourcePortIndex + 1}`);
    this.branchLossStep = step;
    this.branchLossPrevSettings = prevSettings;
    this.branchLossMessage = branchNames.length === 1
      ? `Die Verbindung des Zweigs „${branchNames[0]}" wird entfernt.`
      : `${branchNames.length} Verbindungen der Zweige „${branchNames.join('", „')}" werden entfernt.`;
    return true;
  }

  private closeBranchLossPopup(): void {
    this.branchLossStep = null;
    this.branchLossPrevSettings = null;
    this.branchLossMessage = null;
  }

  /**
   * Schritt- und Ergebnis-Interpretation nach dem Speichern anstoßen. Läuft nur für Hybrid-Regeln
   * mit Schritten.
   *
   * Die Arbeit macht {@link RuleInterpretationService}, nicht diese Komponente: seit dem Übernehmen
   * schließt sich der Editor sofort, die beiden LLM-Aufrufe (Timeout je 90 s) würden also auf einer
   * zerstörten View landen. Der Service arbeitet auf einem eigenen Schnappschuss und persistiert
   * selbst; die Texte liest die Prüfregelübersicht beim nächsten Laden vom Server.
   */
  private interpretRuleAfterSave(projectId: number): void {
    if (!this.isHybridMode) return;
    this.ruleInterpretation.interpretAndPersist(
      this.manifest, this.allModules, this.definitionId, projectId, this.adminGlobal,
    );
  }

  /** Activate the workflow definition */
  activateWorkflow(): void {
    if (this.readOnly) return;
    if (!this.definitionId || this.definitionId === 'new') return;
    this.activating = true;
    this.flowService.activateDefinition(this.definitionId).subscribe({
      next: def => {
        this.definition = def;
        this.activating = false;
        console.log('Workflow aktiviert:', def.id);
      },
      error: err => {
        console.error('Aktivierung fehlgeschlagen:', err);
        this.activating = false;
      },
    });
  }

  /** Deactivate the workflow definition */
  deactivateWorkflow(): void {
    if (this.readOnly) return;
    if (!this.definitionId || this.definitionId === 'new') return;
    this.activating = true;
    this.flowService.deactivateDefinition(this.definitionId).subscribe({
      next: def => {
        this.definition = def;
        this.activating = false;
        console.log('Workflow deaktiviert:', def.id);
      },
      error: err => {
        console.error('Deaktivierung fehlgeschlagen:', err);
        this.activating = false;
      },
    });
  }

  get showLogPanel(): boolean {
    return !!(this.currentInstance && (this.sidebarView === 'test-panel' || this.sidebarView === 'step-detail'));
  }

  /** Cancel the currently running workflow instance */
  cancelWorkflowInstance(): void {
    if (!this.currentInstance?.id) return;
    this.flowService.cancelInstance(this.currentInstance.id).subscribe({
      next: () => {
        console.log('Workflow-Instanz abgebrochen:', this.currentInstance?.id);
        this.stopPolling();
        if (this.currentInstance) {
          this.currentInstance = {...this.currentInstance, status: 'CANCELLED'};
        }
      },
      error: err => console.error('Abbruch fehlgeschlagen:', err),
    });
  }

  /**
   * Verzweigungs-Zweige, deren Flow-Pfad in keiner Nutzerantwort endet.
   *
   * So ein Zweig liefert kein Verdikt. Im Bericht erscheint dieses Ergebnis dann als „unbewertet"
   * (graues Fragezeichen) samt Erklärtext statt als Prüfergebnis — der Lauf selbst gilt als
   * erfolgreich, die Regel sagt aber nichts aus. Deshalb warnen wir hier bereits beim Modellieren.
   * Trifft die Verzweigung zur Laufzeit genau diesen unverbundenen Zweig, benennt das Backend ihn
   * im Ergebnis (WorkflowEngine#unratedVerdict → `unbewertet_schritt`).
   *
   * Bewusst eine WARNUNG und kein Fehler: eine noch unverbundene Sackgasse ist ein legitimer
   * Zwischenstand, das Speichern darf daran nicht scheitern.
   *
   * Schema-getrieben über {@link resolveBranchSpec} — dieselbe Quelle, aus der auch die Flow-Ports
   * gezeichnet werden. Einzige Fachkonstante ist das Terminal-Modul `nutzerantwort`.
   */
  private collectDeadEndBranches(edges: FlowEdge[]): string[] {
    const moduleById = new Map(this.allModules.map(m => [m.moduleId, m]));
    const stepById = new Map((this.manifest.steps ?? []).map(s => [s.nodeId, s]));
    const isTerminal = (nodeId: string) =>
      (stepById.get(nodeId)?.moduleId ?? '').toLowerCase() === NUTZERANTWORT_MODULE_ID;

    // Erreicht der Knoten über Flow-Kanten (Ziel-Port 0) eine Nutzerantwort? Memoisiert, mit
    // Zyklenschutz über den Pfad-Stack.
    const memo = new Map<string, boolean>();
    const reaches = (nodeId: string, onPath: Set<string>): boolean => {
      if (isTerminal(nodeId)) return true;
      const cached = memo.get(nodeId);
      if (cached !== undefined) return cached;
      if (onPath.has(nodeId)) return false; // Zyklus – dieser Weg trägt nichts bei
      onPath.add(nodeId);
      const found = edges
        .filter(e => e.source === nodeId && e.targetPortIndex === 0)
        .some(e => reaches(e.target, onPath));
      onPath.delete(nodeId);
      memo.set(nodeId, found);
      return found;
    };

    const warnings: string[] = [];
    for (const step of this.manifest.steps ?? []) {
      const mod = moduleById.get(step.moduleId ?? '');
      const spec = resolveBranchSpec(mod, step.settings);
      if (!spec) continue;
      const stepLabel = getNodeName(step.inputs, mod);
      spec.cases.forEach((branchCase, portIndex) => {
        const outgoing = edges.filter(e =>
          e.source === step.nodeId && e.sourcePortIndex === portIndex && e.targetPortIndex === 0);
        if (outgoing.length && outgoing.some(e => reaches(e.target, new Set()))) return;
        warnings.push(`Schritt "${stepLabel}": Der Zweig „${branchCase.label}" endet ohne `
          + `Nutzerantwort — dieses Ergebnis erscheint im Bericht als „unbewertet".`);
      });
    }
    return warnings;
  }

  /**
   * Validate + persist the manifest (create or update) and handle the post-save UI.
   *
   * @param afterSave Nachtrag nach erfolgreichem Speichern (KI-Interpretation, siehe
   *   {@link interpretRuleAfterSave}). Der Nachtrag persistiert selbst und läuft deshalb nicht
   *   erneut hier durch.
   * @param closeAfterSave `true` für „Übernehmen" (Editor verlassen), `false` für Teil-Speicherungen
   *   wie das Einstellungs-Popup, die im Editor bleiben sollen.
   */
  private persistWorkflow(
    projectId: number, isNew: boolean, afterSave: () => void, closeAfterSave: boolean,
  ): void {
    const onSaved = (def: WorkflowDefinition) => {
      this.saving = false;
      this.toastService.success('Änderungen gespeichert');
      // Gespeichert ist gespeichert – egal ob über den Banner oder den normalen Knopf.
      this.markRuleProposalApplied();
      // Gespeichert ist der neue Ausgangsstand für „Stand: nicht übernommen" im Testlauf.
      if (this.ruleTestKey) this.ruleTestInitialManifest = JSON.stringify(this.ruleTestManifestSnapshot());
      if (isNew && def.id) {
        this.definitionId = def.id;
        this.definition = def;
        // Nur wenn der Editor offen bleibt, muss die URL auf die neue ID umgeschrieben werden;
        // beim Übernehmen verlassen wir die Route ohnehin.
        if (!closeAfterSave) {
          this.router.navigate(['..', def.id], {relativeTo: this.route, replaceUrl: true});
        }
      }
      // Nachtrag erst nach der ID-Übernahme starten, damit der zweite PUT die neue Definition
      // aktualisiert statt eine weitere anzulegen. Er läuft im Service weiter, auch wenn diese
      // Komponente durch die folgende Navigation zerstört wird.
      afterSave();
      if (closeAfterSave) this.goBack();
    };
    const onError = (err: any) => {
      console.error('Speichern fehlgeschlagen:', err);
      this.saving = false;
      this.toastService.error('Speichern fehlgeschlagen');
    };

    // Admin-Modus: projektlos über die globalen Endpunkte, ohne projekt-scoped Validierung.
    if (this.adminGlobal) {
      const save$ = isNew
        ? this.rulesetService.createGlobalHybridDefinition(this.manifest, 'HYBRID_RULE')
        : this.rulesetService.updateGlobalHybridDefinition(this.definitionId, this.manifest);
      save$.subscribe({next: onSaved, error: onError});
      return;
    }

    this.flowService.validateManifest(this.manifest, projectId).subscribe({
      next: value => {
        const save$ = isNew
          ? this.flowService.saveDefinition(this.manifest, projectId)
          : this.flowService.updateDefinition(this.definitionId, this.manifest, projectId);
        save$.subscribe({next: onSaved, error: onError});
      },
      error: err => {
        console.error('Validierung fehlgeschlagen:', err);
        this.saving = false;
        this.toastService.error('Validierung fehlgeschlagen');
      },
    });
  }

  /**
   * Schreibt das im Regelset gepflegte KI-Modell in die Settings jedes KI-Anweisung-Knotens.
   *
   * Das Modell wird einmal pro Set gepflegt und im Knoten nur als read-only Badge gezeigt. Ohne
   * diesen Abgleich zeigte der Badge den Wert, der beim Anlegen des Knotens eingefroren wurde
   * (bzw. den Schema-Default) — also z.B. weiter „gpt-5.4-mini", obwohl im Set längst Luna steht.
   */
  private applySetAiModel(): void {
    const model = this.hybridSetContext?.kiModel;
    if (!model) return;
    for (const step of this.manifest?.steps ?? []) {
      if ((step.moduleId ?? '').toLowerCase() !== AI_INSTRUCTION_MODULE_ID) continue;
      const settings = step.settings ?? (step.settings = {});
      settings['modell'] = model;
    }
  }

  /** Liest die Set-Eingabeparameter/Prüfebene/Identifikator/KI-Modell aus der Regelset-Definition. */
  private extractSetParamContext(setDef: any): {
    params: SetParamDef[];
    identifikatorParamId: string;
    pruefebene: string;
    kiModel: string;
    allowedFileTypes: string[];
  } {
    const manifest = this.flowService.parseManifest(setDef);
    const raw = manifest?.trigger?.config?.['hybridEditor'] ?? (manifest as any)?.config ?? {};
    const cfg = typeof raw === 'string' ? this.safeParseJson(raw) : raw;
    const params: SetParamDef[] = Array.isArray(cfg?.eingabeparameter)
      ? cfg.eingabeparameter.map((p: any) => ({
        id: String(p?.id ?? ''),
        name: String(p?.name ?? ''),
        beschreibung: String(p?.beschreibung ?? ''),
        dataType: String(p?.dataType ?? p?.typ ?? 'Text'),
        // Auswahl-Optionen des Sets mitnehmen: reconcileEingangParameters schreibt sie als
        // `optionen` in den Knoten, und daraus wird das Enum der Extraktion.
        options: Array.isArray(p?.options) ? p.options.map((o: any) => String(o ?? '')) : [],
      }))
      : [];
    // Erlaubte Dateitypen: die regelbezogene Angabe gewinnt, sonst die des Sets. In der
    // Hybrid-Route ist `definitionId` die ruleId (siehe ngOnInit), also der Schlüssel in
    // `ruleAllowedFileTypes`. Sie entscheiden, ob der Eingangsparameter-Knoten im Datei-Modus
    // läuft — deshalb müssen sie in den Knoten gespiegelt werden wie Parameter und Prüfebene.
    const ruleFileTypes = cfg?.ruleAllowedFileTypes?.[this.definitionId];
    const rawFileTypes = Array.isArray(ruleFileTypes) && ruleFileTypes.length
      ? ruleFileTypes
      : (Array.isArray(cfg?.allowedFileTypes) ? cfg.allowedFileTypes : []);

    return {
      params,
      identifikatorParamId: String(cfg?.identifikatorParamId ?? ''),
      pruefebene: String(cfg?.pruefebene ?? 'lv_position'),
      kiModel: String(cfg?.kiModel ?? ''),
      allowedFileTypes: rawFileTypes.map((t: any) => String(t ?? '')).filter(Boolean),
    };
  }

  private safeParseJson(value: string): any {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }

  /**
   * Nach einer neuen Verbindung leere Felder des Ziels aus dem Vorgänger vorbelegen (siehe
   * {@link applyUpstreamPrefill}) – z.B. Status und Antworttext einer Nutzerantwort hinter der
   * IFC-Prüfung. Die Kante steht zu diesem Zeitpunkt schon im Manifest (Canvas `rebuildEdges`).
   */
  onConnectionCreated(event: { targetNodeId: string }): void {
    if (this.readOnly) return;
    const filled = applyUpstreamPrefill(this.manifest, this.allModules, event.targetNodeId);
    if (!filled.keys.length) return;
    this.canvas?.refreshNode(event.targetNodeId);
    this.toastService.info(`Aus „${filled.sourceName}" vorbelegt – bitte prüfen und bei Bedarf anpassen.`);
  }

  onNodeSelected(nodeId: string | null) {
    if (!nodeId) {
      this.selectedStep = null;
      this.selectedTrigger = null;
      this.selectedModule = null;
      this.selectedStepLog = null;
      if (this.viewMode === 'log') {
        // In log mode: deselecting shows the full test panel overview
        this.sidebarView = 'test-panel';
      } else if (this.sidebarView === 'node-config') {
        this.sidebarView = 'modules';
      }
      return;
    }

    // In log mode: show step details for the clicked node
    if (this.viewMode === 'log') {
      const stepLog = this.currentInstance?.steps?.find(s => s.nodeId === nodeId);
      if (stepLog) {
        this.selectedStepLog = stepLog;
        this.sidebarView = 'step-detail';
      }
      return;
    }

    // Normal edit mode: show node config
    const triggerId = this.manifest.trigger?.nodeId ?? 'trigger_0';
    if (nodeId === triggerId) {
      this.selectedTrigger = this.manifest.trigger!;
      this.selectedStep = null;
      this.selectedModule = this.allModules.find(m => m.moduleId === this.manifest.trigger?.moduleId) ?? null;
    } else {
      const step = (this.manifest.steps ?? []).find(s => s.nodeId === nodeId);
      if (step) {
        this.selectedStep = step;
        this.selectedTrigger = null;
        this.selectedModule = this.allModules.find(m => m.moduleId === step.moduleId) ?? null;
      }
    }

    if (this.selectedStep || this.selectedTrigger) {
      this.sidebarView = 'node-config';
    }
  }

  /** Trigger the workflow and start polling for results */
  triggerWorkflow(triggerData?: { [key: string]: any }): void {
    if (this.readOnly) return;
    if (!this.definitionId || this.definitionId === 'new') return;
    this.triggering = true;
    this.currentInstance = null;
    this.flowService.triggerWorkflow(this.definitionId, triggerData).subscribe({
      next: instance => {
        this.currentInstance = instance;
        this.triggering = false;
        this.viewMode = 'log';
        this.sidebarView = 'test-panel';
        console.log('Workflow gestartet, Instanz:', instance.id);
        this.startPolling();
      },
      error: err => {
        console.error('Trigger fehlgeschlagen:', err);
        this.triggering = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.ruleTestSubscriptions.unsubscribe();
    this.unregisterRuleTestEditor?.();
    // Der Edit-Chat gehört zum ganzen hybriden myCheck: zwischen Set- und Regel-Editor bleibt er offen,
    // beim Verlassen des myChecks fällt er auf den klassischen Chat zurück (wie im Set-Editor).
    if (this.isHybridMode && !this.chatService.navigationStaysInHybridMycheck(this.router, this.rulesetId)) {
      this.chatService.closeMycheckSurface();
    }
  }


  // ─── chatBIC-Vorschlag ───────────────────────────────────────────────────────

  /** Ein beim Öffnen bereits offener Vorschlag dieser Regel wird gleich gezeigt. */
  private loadPendingRuleProposal(): void {
    const projectId = this.proposalProjectId();
    if (!projectId) return;
    this.proposalService.getLatestPending(this.definitionId, projectId).subscribe({
      next: proposal => {
        if (proposal) this.applyRuleProposal(proposal);
      },
      error: err => console.error('Offener Vorschlag konnte nicht geladen werden:', err),
    });
  }

  private loadRuleProposal(proposalId: string): void {
    const projectId = this.proposalProjectId();
    if (!projectId) return;
    this.proposalService.getById(this.definitionId, proposalId, projectId).subscribe({
      next: proposal => {
        if (proposal.status === 'PENDING') this.applyRuleProposal(proposal);
      },
      error: err => console.error('Vorschlag konnte nicht geladen werden:', err),
    });
  }

  /** Vorschläge gibt es nur für gespeicherte Projekt-Regeln, nicht read-only und nicht im Admin-Bereich. */
  private proposalProjectId(): number | null {
    if (!this.isHybridMode || this.readOnly || this.adminGlobal || !this.definitionId || this.definitionId === 'new') {
      return null;
    }
    return this.stateConfigService.getProjectIdOrUndefined() ?? null;
  }

  /**
   * Legt den vorgeschlagenen Ablauf auf die Leinwand und markiert, was sich ändert. Gespeichert wird
   * erst über „Übernehmen" (Banner) bzw. den normalen Speichern-Weg.
   */
  applyRuleProposal(proposal: MyCheckProposal): void {
    const proposed = proposal.document?.manifest as Manifest | undefined;
    if (!proposed?.steps || !this.manifest) return;
    if (this.pendingProposal) this.revertRuleProposal();

    const syncedCurrent = this.currentManifestWithEdges();
    this.preProposalManifest = JSON.stringify(syncedCurrent);
    const next: Manifest = JSON.parse(JSON.stringify(proposed));
    // Der Eingangsparameter-Knoten folgt dem Set (siehe ngOnInit) – nur der erste Schritt kommt aus
    // dem Vorschlag. Der Server lehnt eine geänderte Trigger-Konfiguration ohnehin ab.
    next.trigger = {...syncedCurrent.trigger!, onSuccess: next.trigger?.onSuccess ?? syncedCurrent.trigger?.onSuccess};

    const diff = computeRuleProposalDiff(syncedCurrent, next);
    if (!diff.hasChanges) {
      this.toastService.info('Der Vorschlag von chatBIC ändert an dieser Regel nichts.');
      return;
    }
    this.manifest = next;
    this.applySetAiModel();
    this.proposalMarks = diff.marks;
    this.proposalRemovedNames = diff.removed.map(step => getNodeName(step.inputs, this.allModules.find(m => m.moduleId === step.moduleId)));
    this.pendingProposal = proposal;
    this.clearSelection();
    this.rebuildCanvasFromManifest();
  }

  /** Banner „Übernehmen": speichern und im Editor bleiben – der Vorschlag gilt dann als übernommen. */
  onRuleProposalApplyClicked(): void {
    if (!this.pendingProposal) return;
    this.saveWorkflow(false);
  }

  onRuleProposalDiscardClicked(): void {
    const proposal = this.pendingProposal;
    if (!proposal) return;
    this.revertRuleProposal();
    const projectId = this.proposalProjectId();
    if (projectId) {
      this.proposalService.discard(this.definitionId, proposal.id, projectId).subscribe({
        error: err => console.error('Verwerfen im Backend fehlgeschlagen (Leinwand bereits zurückgesetzt):', err),
      });
    }
  }

  private revertRuleProposal(): void {
    if (this.preProposalManifest) {
      this.manifest = JSON.parse(this.preProposalManifest);
      this.clearSelection();
      this.rebuildCanvasFromManifest();
    }
    this.clearRuleProposalState();
  }

  private markRuleProposalApplied(): void {
    const proposal = this.pendingProposal;
    if (!proposal) return;
    this.clearRuleProposalState();
    const projectId = this.proposalProjectId();
    if (!projectId) return;
    this.proposalService.apply(this.definitionId, proposal.id, projectId).subscribe({
      error: err => console.error('Vorschlag konnte nicht als übernommen markiert werden (Regel ist gespeichert):', err),
    });
  }

  private clearRuleProposalState(): void {
    this.pendingProposal = null;
    this.proposalMarks = {};
    this.proposalRemovedNames = [];
    this.preProposalManifest = null;
  }

  private clearSelection(): void {
    this.onNodeSelected(null);
    this.canvas?.clearSelection();
  }

  /** Das Manifest mit den Kanten der Leinwand – sonst fehlten im Vergleich frisch gezogene Kanten. */
  private currentManifestWithEdges(): Manifest {
    const copy: Manifest = JSON.parse(JSON.stringify(this.manifest));
    syncEdgesToManifest(copy, this.canvas?.edges ?? this.edges, this.allModules);
    return copy;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   TESTLAUF („Prüfregel testen", #2356)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Nur für eine Regel eines Hybrid-Sets im Projektkontext (Route `:rulesetId/hybrid/:ruleId`),
   * bearbeitbar, im Bearbeitungsmodus und nicht beim Hineinzoomen in einen Sub-Workflow.
   */
  get canRuleTest(): boolean {
    return !!this.ruleTestKey && !this.readOnly && !this.loading && this.viewMode === 'edit'
      && this.drillDownStack.length === 0 && !!this.ruleTestSetDef && !!this.manifest;
  }

  get ruleTestLimitReached(): boolean {
    return this.ruleTestQuota?.remaining === 0;
  }

  /** Tooltip am deaktivierten Button (Figma 06); `undefined` blendet ihn aus – ein Leerstring zeigte einen leeren Pfeil. */
  get ruleTestTooltip(): string {
    return ruleTestLimitTooltip(this.ruleTestQuota)?.title ?? undefined as unknown as string;
  }

  get ruleTestTooltipSubtext(): string {
    return ruleTestLimitTooltip(this.ruleTestQuota)?.text ?? undefined as unknown as string;
  }

  private initRuleTest(): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    const isRule = this.isHybridMode && !!this.rulesetId && !!this.definitionId && this.definitionId !== 'new';
    if (!isRule || this.adminGlobal || !projectId) return;
    this.ruleTestKey = ruleTestContextKey(projectId, this.rulesetId, this.definitionId);
    this.pendingRuleTestRunId = this.route.snapshot.queryParamMap.get('ruleTest');
    this.ruleTestSubscriptions.add(this.ruleTestService.quota$.subscribe(quota => this.ruleTestQuota = quota));
    this.ruleTestSubscriptions.add(
      this.ruleTestService.hasUnseenResult$(this.ruleTestKey).subscribe(unseen => this.ruleTestUnseen = unseen),
    );
    this.unregisterRuleTestEditor = this.ruleTestService.registerEditor(
      this.ruleTestKey, [(this.router.url ?? '').split('?')[0]], () => this.openRuleTest(),
    );
    this.ruleTestService.refreshQuota();
  }

  private openPendingRuleTest(): void {
    const runId = this.pendingRuleTestRunId;
    this.pendingRuleTestRunId = null;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!runId || !projectId || !this.canRuleTest) return;
    this.ruleTestService.attach(projectId, this.ruleTestKey, runId);
    this.openRuleTest();
  }

  openRuleTest(): void {
    if (!this.canRuleTest) return;
    this.ruleTestContext = this.buildRuleTestContext();
  }

  closeRuleTest(): void {
    this.ruleTestContext = null;
  }

  /**
   * Der ungespeicherte Workflow dieser Regel: eine Kopie des Manifests mit den aktuellen Kanten der
   * Leinwand, genau so, wie `saveWorkflow()` sie vor dem Speichern hineinschreibt – nur ohne das
   * lebende Manifest anzufassen.
   */
  private ruleTestManifestSnapshot(): Manifest {
    const copy = structuredClone(this.manifest);
    syncEdgesToManifest(copy, this.canvas?.edges ?? this.edges, this.allModules);
    return copy;
  }

  /** Set-Konfiguration des gespeicherten Hybrid-Sets (`trigger.config.hybridEditor`). */
  private ruleTestSetConfig(): Record<string, any> {
    if (!this.ruleTestSetDef) return {};
    const manifest = this.flowService.parseManifest(this.ruleTestSetDef);
    const raw = manifest?.trigger?.config?.['hybridEditor'] ?? (manifest as any)?.config ?? {};
    const cfg = typeof raw === 'string' ? this.safeParseJson(raw) : raw;
    return cfg && typeof cfg === 'object' ? cfg : {};
  }

  /**
   * Friert den Stand ein: der ungespeicherte Workflow dieser Regel plus die GESPEICHERTE
   * Set-Konfiguration (Plan E3). Die Regel steht fest, das Prüfregel-Select ist gesperrt.
   */
  buildRuleTestContext(): RuleTestPopupContext {
    const projectId = this.stateConfigService.getProjectIdOrUndefined() ?? 0;
    const ruleManifest = this.ruleTestManifestSnapshot();
    const setConfig = structuredClone(this.ruleTestSetConfig());
    const ruleKey = this.definitionId;
    const editorDirty = JSON.stringify(ruleManifest) !== this.ruleTestInitialManifest;
    const ruleFileTypes = setConfig['ruleAllowedFileTypes']?.[ruleKey];
    const ruleDocumentClasses = setConfig['ruleAllowedDocumentClasses']?.[ruleKey];
    const edges = this.canvas?.edges ?? this.edges;
    return {
      projectId,
      projectName: this.stateConfigService.getProject()?.name ?? '',
      rulesetType: 'HYBRID',
      rulesetId: this.rulesetId,
      setLabel: this.ruleTestSetDef?.name ?? '',
      rules: [{
        key: ruleKey,
        shortCode: String(setConfig['ruleShortCodes']?.[ruleKey] ?? ''),
        name: ruleManifest.name ?? this.workflowName,
        allowedFileTypes: Array.isArray(ruleFileTypes) ? ruleFileTypes : [],
        allowedDocumentClasses: Array.isArray(ruleDocumentClasses) ? ruleDocumentClasses : [],
      }],
      preselectedRuleKey: ruleKey,
      ruleLocked: true,
      setAllowedFileTypes: Array.isArray(setConfig['allowedFileTypes']) ? setConfig['allowedFileTypes'] : [],
      setAllowedDocumentClasses: Array.isArray(setConfig['allowedDocumentClasses']) ? setConfig['allowedDocumentClasses'] : [],
      editorDirty,
      modelLabel: setConfig['kiModel'] ? String(setConfig['kiModel']) : null,
      validationErrors: [
        ...collectMissingInputs(ruleManifest, this.allModules),
        ...collectMissingSettings(ruleManifest, this.allModules),
        ...collectHybridCompletenessErrors(ruleManifest, this.allModules, edges),
      ],
      contextKey: this.ruleTestKey,
      buildRequest: (key, files) => ({
        rulesetType: 'HYBRID',
        rulesetId: this.rulesetId,
        ruleKey: key,
        editorDirty,
        files,
        hybrid: {setConfig, ruleManifest},
      }),
      reportConfig: buildDefaultReportConfigForRuleset(
        this.rulesetId, 'HYBRID', setConfig['pruefebene'], setConfig['outputParameters'], setConfig['checkMode'],
      ),
    };
  }

  onTestPanelClosed(): void {
    this.currentInstance = null;
    this.selectedStepLog = null;
    this.viewMode = 'edit';
    this.stopPolling();
    this.sidebarView = 'modules';
  }

  /** Back from step-detail to test-panel overview */
  onStepDetailBack(): void {
    this.selectedStepLog = null;
    this.sidebarView = 'test-panel';
    if (this.canvas) {
      this.canvas.clearSelection();
    }
  }

  /** Handle double-click on a canvas node — drill into child if HYBRID_RULESET_EXECUTOR */
  onNodeDoubleClicked(nodeId: string): void {
    if (this.viewMode !== 'log') return;
    const stepLog = this.currentInstance?.steps?.find(s => s.nodeId === nodeId);
    if (!stepLog || stepLog.moduleId !== 'HYBRID_RULESET_EXECUTOR') return;

    // Find first child instance to drill into
    if (this.hybridChildInstances.length > 0) {
      this.drillIntoChild(this.hybridChildInstances[0]);
    }
  }

  /** Get module name for a step log */
  getStepModuleName(step: StepLogDto): string {
    if (!step.moduleId) return step.nodeId ?? 'Unbekannt';
    const mod = this.allModules.find(m => m.moduleId === step.moduleId);
    return mod?.name ?? step.moduleId;
  }

  /** Get formatted duration for a step log */
  getStepDurationMs(step: StepLogDto): string {
    if (!step.startedAt || !step.completedAt) return '';
    const ms = new Date(step.completedAt as any).getTime() - new Date(step.startedAt as any).getTime();
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  }

  /** Check if an object has non-internal entries */
  hasStepEntries(data: any): boolean {
    if (!data || typeof data !== 'object') return false;
    return Object.keys(data).filter(k => !k.startsWith('__')).length > 0;
  }

  /** Get display entries from step data */
  getStepDisplayEntries(data: any): { key: string; value: string }[] {
    if (!data || typeof data !== 'object') return [];
    return Object.entries(data)
      .filter(([key]) => !key.startsWith('__'))
      .map(([key, value]) => ({
        key,
        value: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? ''),
      }));
  }

  /** Drill into a child workflow instance (loads its definition into the canvas) */
  drillIntoChild(childInstance: WorkflowInstanceDto): void {
    if (!childInstance.definitionId) return;

    // Push current state onto stack
    this.drillDownStack.push({
      definitionId: this.definitionId,
      definition: this.definition,
      manifest: this.manifest,
      edges: this.edges,
      currentInstance: this.currentInstance,
      hybridChildInstances: this.hybridChildInstances,
      workflowName: this.workflowName,
    });

    // Load child's definition
    this.flowService.getDefinition(childInstance.definitionId).subscribe({
      next: (def) => {
        this.definition = def;
        this.definitionId = def.id!;
        this.manifest = this.flowService.parseManifest(def);
        this.edges = buildEdgesFromManifest(this.manifest, this.allModules);
        this.currentInstance = childInstance;
        this.hybridChildInstances = [];
        this.selectedStepLog = null;
        this.sidebarView = 'test-panel';

        // Rebuild canvas
        setTimeout(() => {
          if (this.canvas) {
            this.canvas.manifest = this.manifest;
            this.canvas.edges = this.edges;
            this.canvas.renderedNodes = [];
            this.canvas.buildGraph();
            this.fitCanvasDelayed();
          }
        }, 100);
      }
    });
  }

  /** Navigate back up the drill-down stack */
  drillBack(): void {
    const entry = this.drillDownStack.pop();
    if (!entry) return;

    this.definitionId = entry.definitionId;
    this.definition = entry.definition;
    this.manifest = entry.manifest;
    this.edges = entry.edges;
    this.currentInstance = entry.currentInstance;
    this.hybridChildInstances = entry.hybridChildInstances;
    this.selectedStepLog = null;
    this.sidebarView = 'test-panel';

    // Rebuild canvas with parent manifest
    setTimeout(() => {
      if (this.canvas) {
        this.canvas.manifest = this.manifest;
        this.canvas.edges = this.edges;
        this.canvas.renderedNodes = [];
        this.canvas.buildGraph();
        this.fitCanvasDelayed();
      }
    }, 100);
  }

  /**
   * For hybrid parent instances: show the parent workflow in the canvas
   * and pre-load child instances for the test panel's expandable details.
   */
  private loadHybridChildView(parentInstance: WorkflowInstanceDto): void {
    this.hybridParentInstance = parentInstance;
    this.currentInstance = parentInstance;
    this.sidebarView = 'test-panel';

    // Pre-load child instances so test panel can show them immediately
    this.flowService.getChildInstances(parentInstance.id!).subscribe({
      next: (children) => {
        this.hybridChildInstances = children;
      },
      error: () => {
        this.hybridChildInstances = [];
      }
    });

    this.fitCanvasDelayed();
  }

  private fitCanvasDelayed(): void {
    setTimeout(() => {
      if (this.canvas) {
        this.canvas.autoLayout();
        // Das Panel schwebt über der Canvas – ohne Abzug passt „Einpassen" auf eine Breite, deren
        // rechter Teil verdeckt ist (dieselbe Größe wie beim Auto-Panning, siehe sidebarOverlayWidth).
        this.canvas.fitToView(this.sidebarOverlayWidth);
      }
    }, 800);
  }

  /** Start polling for instance status updates every 2 seconds */
  private startPolling(): void {
    this.stopPolling();
    if (!this.currentInstance?.id) return;
    this.instancePolling = true;
    const instanceId = this.currentInstance.id;

    this.pollingSubscription = setInterval(() => {
      this.flowService.getInstance(instanceId).subscribe({
        next: instance => {
          this.currentInstance = instance;
          if (this.isTerminalStatus(instance.status)) {
            this.stopPolling();
            console.log('Workflow abgeschlossen:', instance.status, instance.outputData);
          }
        },
        error: err => {
          console.error('Polling fehlgeschlagen:', err);
          this.stopPolling();
        },
      });
    }, 2000);
  }

  /** Stop the polling interval */
  private stopPolling(): void {
    if (this.pollingSubscription !== null) {
      clearInterval(this.pollingSubscription);
      this.pollingSubscription = null;
    }
    this.instancePolling = false;
  }

  /** Check if a status is terminal */
  private isTerminalStatus(status?: string): boolean {
    return status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED';
  }
}
